import { TbCalendarStats, TbDeviceAnalytics } from '@common-web/icon/icons';
import {
  IconCpReviews,
  IconDashboard,
  IconFinance,
  IconOrdersList,
  IconProductsList,
  IconRoles,
  IconUserSettings,
} from '@common-web/icons';
import {
  ADD_SELLER_PATH,
  ADVANCE_PAYMENT_PATH,
  ALL_IMAGES_PATH,
  CALLS_PATH,
  CARTS,
  CATEGORIES_MODERATION_PATH,
  ACCOUNT_STATEMENT_FINANCE as CP_ACCOUNT_STATEMENT_FINANCE,
  ORDER_OVERVIEW_FINANCE as CP_ORDER_OVERVIEW_FINANCE,
  FOLLOW_UPS_PATH,
  LOCATIONS_PATH,
  PRODUCTS_PATH,
  REAL_TIME_STATS_PATH,
  RETURN_ORDERS_PATH,
  REVIEWS,
  SALES_REPORT_PATH,
  SELLER_APPEALS_PATH,
  SELLERS_LISTING_PATH,
  TYPE_MODERATION_PATH,
  UPLOADS_MODERATION_PATH,
  WEIGHT_MODERATION_PATH,
} from '@constants/cp-paths';
import {
  ACTION_CREATE,
  ACTION_CURRENT_DAY_ORDER,
  ACTION_GROUPED_BY_WEIGHT_RANGE,
  ACTION_INDEX,
  ACTION_LIFETIME_ORDER,
  ACTION_MISSING_CATEGORY_FIELDS,
  ACTION_SALES_REPORT,
  ACTION_SHOW,
  ACTION_STORE_CATEGORIES,
  ACTION_TRANSACTION_HISTORY,
  PERMISSION_KEY_ADMIN,
  PERMISSION_KEY_ATTACHMENT,
  PERMISSION_KEY_CART,
  PERMISSION_KEY_CATEGORY,
  PERMISSION_KEY_CONSIGNMENT,
  PERMISSION_KEY_DASHBOARD,
  PERMISSION_KEY_FOLLOW_UP,
  PERMISSION_KEY_LOCATION,
  PERMISSION_KEY_ORDER,
  PERMISSION_KEY_PAYMENT,
  PERMISSION_KEY_PRODUCT,
  PERMISSION_KEY_RECORDINGS,
  PERMISSION_KEY_REPORT,
  PERMISSION_KEY_RETURN,
  PERMISSION_KEY_REVIEW,
  PERMISSION_KEY_ROLE,
  PERMISSION_KEY_SETTLEMENT,
  PERMISSION_KEY_STORE,
  PERMISSION_KEY_TASK,
  PERMISSION_KEY_TODO,
  PERMISSION_KEY_USER,
} from '@constants/permission-constants';
import {
  ACCOUNT_STATEMENT_FINANCE,
  ADD_PRODUCT,
  CATEGORY_COMMISSION,
  CHAT_PATH,
  DELIVERY_SETTINGS,
  MANAGE_ORDERS,
  MANAGE_PRODUCTS,
  MANAGE_USERS,
  OMS_DASHBOARD_PATH,
  ORDER_OVERVIEW_FINANCE,
  PERFORMANCE_DASHBOARD,
  PROFILE_SETTINGS,
  REVIEWS_PRODUCT,
  SC_RETURN_ORDERS,
  SHIPMENT_ORDERS,
} from '@constants/seller-center-paths';
import { strings } from '@constants/strings';
import { isDubizzleTenant } from '@utils/utils';
import tenantConfig from 'data/customizer/tenant-config';
import { BsCart3, BsGraphUpArrow } from 'react-icons/bs';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import PermissionsHelper from '../../../../auth/helper/PermissionHelper.js';
import { DASHBOARD_PATH, ROLES_PATH, USERS_PATH } from '../../../../constants/cp-paths';
import styles from '../index.module.scss';

const showStoreUploads = !!tenantConfig?.side_menu?.cp?.store_uploads?.show;
const allowLocationSettingsUpdate = !!tenantConfig?.side_menu?.cp?.location_settings?.show;

const hasAnyChildPermissions = (children) => {
  return children && children.length > 0;
};

const getSellerCenterProductPermissions = (t) => {
  const productPermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_PRODUCT, ACTION_INDEX)) {
    productPermissions.push({
      key: 'manage-products',
      label: <Link to={MANAGE_PRODUCTS}>{t(strings.manage_products)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_PRODUCT, ACTION_CREATE)) {
    productPermissions.push({
      key: 'add-product',
      label: <Link to={ADD_PRODUCT}>{t(strings.add_product)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_REVIEW, ACTION_INDEX)) {
    productPermissions.push({
      key: 'reviews',
      label: <Link to={REVIEWS_PRODUCT}>{t(strings.reviews)}</Link>,
    });
  }
  return productPermissions;
};

const getSellerCenterOrdersPermissions = (t) => {
  const orderPermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_ORDER, ACTION_INDEX)) {
    orderPermissions.push({
      key: 'manage-orders',
      label: <Link to={MANAGE_ORDERS}>{t(strings.manage_orders)}</Link>,
    });
  }
  if (
    !!tenantConfig?.side_menu?.seller_center?.return_orders &&
    PermissionsHelper.getPermissions(PERMISSION_KEY_RETURN, ACTION_INDEX)
  ) {
    orderPermissions.push({
      key: 'return-orders',
      label: <Link to={SC_RETURN_ORDERS}>{t(strings.return_orders)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_CONSIGNMENT, ACTION_INDEX)) {
    orderPermissions.push({
      key: 'shipments',
      label: <Link to={SHIPMENT_ORDERS}>{t(strings.shipments)}</Link>,
    });
  }
  return orderPermissions;
};

const getSellerCenterFinancePermissions = (t) => {
  const financePermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_ORDER, ACTION_TRANSACTION_HISTORY)) {
    financePermissions.push({
      key: 'transaction-history',
      label: <Link to={ORDER_OVERVIEW_FINANCE}>{t(strings.transaction_history)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_SETTLEMENT, ACTION_INDEX)) {
    financePermissions.push({
      key: 'account-statement',
      label: <Link to={ACCOUNT_STATEMENT_FINANCE}>{t(strings.account_statement)}</Link>,
    });
  }
  return financePermissions;
};

const getSellerCenterSettingsPermissions = (t) => {
  const settingsPermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_USER, ACTION_INDEX)) {
    settingsPermissions.push({
      key: 'manage-users',
      label: <Link to={`${MANAGE_USERS}?f[slug]=active`}>{t(strings.manage_users)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_STORE, ACTION_SHOW)) {
    settingsPermissions.push({
      key: 'profile-settings',
      label: <Link to={PROFILE_SETTINGS}>{t(strings.profile_settings)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_STORE, ACTION_STORE_CATEGORIES)) {
    settingsPermissions.push({
      key: 'category-commission',
      label: <Link to={CATEGORY_COMMISSION}>{t(strings.commissions)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_STORE, ACTION_SHOW) && isDubizzleTenant()) {
    settingsPermissions.push({
      key: 'delivery-settings',
      label: <Link to={DELIVERY_SETTINGS}>{t(strings.delivery_settings)}</Link>,
    });
  }
  return settingsPermissions;
};

export const getCPSellerPermissions = (t) => {
  const sellerPermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_STORE, ACTION_INDEX)) {
    sellerPermissions.push({
      key: 'all-sellers',
      label: <Link to={SELLERS_LISTING_PATH}>{t(strings.all_sellers)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_STORE, ACTION_CREATE)) {
    sellerPermissions.push({
      key: 'add-seller',
      label: <Link to={ADD_SELLER_PATH}>{t(strings.add_seller)}</Link>,
    });
  }
  sellerPermissions.push({
    key: 'seller-appeals',
    label: <Link to={SELLER_APPEALS_PATH}>{t(strings.appeals)}</Link>,
  });

  return sellerPermissions;
};

export const getCPFinancePermissions = (t) => {
  const financePermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_ORDER, ACTION_TRANSACTION_HISTORY)) {
    financePermissions.push({
      key: 'transaction-history',
      label: <Link to={CP_ORDER_OVERVIEW_FINANCE}>{t(strings.transaction_history)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_SETTLEMENT, ACTION_INDEX)) {
    financePermissions.push({
      key: 'account-statement',
      label: <Link to={CP_ACCOUNT_STATEMENT_FINANCE}>{t(strings.account_statements)}</Link>,
    });
  }

  return financePermissions;
};

export const getCPReportsPermissions = (t) => {
  const reportsPermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_REPORT, ACTION_SALES_REPORT)) {
    reportsPermissions.push({
      key: 'sales-report',
      label: <Link to={SALES_REPORT_PATH}>{t(strings.sales_report)}</Link>,
    });
  }

  return reportsPermissions;
};

export const getCPProductPermissions = (t) => {
  const productPermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_PRODUCT, ACTION_INDEX)) {
    productPermissions.push({
      key: 'listing',
      label: <Link to={PRODUCTS_PATH}>{t(strings.products)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_REVIEW, ACTION_INDEX)) {
    productPermissions.push({
      key: 'reviews',
      label: <Link to={REVIEWS}>{t(strings.reviews)}</Link>,
    });
  }

  return productPermissions;
};

const getCPSettingsChildren = (t) => {
  const settingChildren = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_ROLE, ACTION_INDEX)) {
    settingChildren.push({
      key: 'roles',
      label: <Link to={ROLES_PATH}>{t(strings.roles)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_ADMIN, ACTION_INDEX)) {
    settingChildren.push({
      key: 'users',
      label: <Link to={USERS_PATH}>{t(strings.users)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_LOCATION, ACTION_INDEX) && allowLocationSettingsUpdate) {
    settingChildren.push({
      key: 'locations',
      label: <Link to={LOCATIONS_PATH}>{t(strings.locations)}</Link>,
    });
  }
  return settingChildren;
};

export const getCPModerationPermissions = (t) => {
  const moderationPermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_ATTACHMENT, ACTION_INDEX)) {
    moderationPermissions.push({
      key: 'attachments',
      label: <Link to={ALL_IMAGES_PATH}>{t('Attachments')}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_CATEGORY, ACTION_INDEX)) {
    moderationPermissions.push({
      key: 'categories-moderation',
      label: <Link to={CATEGORIES_MODERATION_PATH}>{t(strings.categories_moderation)}</Link>,
    });
  }
  if (showStoreUploads && PermissionsHelper.getPermissions(PERMISSION_KEY_STORE, ACTION_INDEX)) {
    moderationPermissions.push({
      key: 'uploads-moderation',
      label: <Link to={UPLOADS_MODERATION_PATH}>{t(strings.store_uploads)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_CATEGORY, ACTION_MISSING_CATEGORY_FIELDS)) {
    moderationPermissions.push({
      key: 'type-moderation',
      label: <Link to={TYPE_MODERATION_PATH}>{t(strings.type_moderation)}</Link>,
    });
  }
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_PRODUCT, ACTION_GROUPED_BY_WEIGHT_RANGE)) {
    moderationPermissions.push({
      key: 'weight-moderation',
      label: <Link to={WEIGHT_MODERATION_PATH}>{t(strings.weight_moderation)}</Link>,
    });
  }
  return moderationPermissions;
};

export const getCPOrdersPermissions = (t) => {
  const ordersPermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_ORDER, ACTION_INDEX)) {
    ordersPermissions.push({
      key: 'orders-listing',
      label: <Link to={tenantConfig?.side_menu?.cp?.orders?.path}>{t(strings.orders)}</Link>,
    });
  }

  if (
    !!tenantConfig?.side_menu?.cp?.advance_payment &&
    PermissionsHelper.getPermissions(PERMISSION_KEY_PAYMENT, ACTION_INDEX)
  ) {
    ordersPermissions.push({
      key: 'advance-payment',
      label: <Link to={ADVANCE_PAYMENT_PATH}>{t(strings.payments)}</Link>,
    });
  }

  if (
    !!tenantConfig?.side_menu?.cp?.return_orders &&
    PermissionsHelper.getPermissions(PERMISSION_KEY_RETURN, ACTION_INDEX)
  ) {
    ordersPermissions.push({
      key: 'return-orders',
      label: <Link to={RETURN_ORDERS_PATH}>{t(strings.return_orders)}</Link>,
    });
  }

  if (
    PermissionsHelper.getPermissions(PERMISSION_KEY_RECORDINGS, ACTION_INDEX) &&
    !!tenantConfig?.side_menu?.cp?.calls
  ) {
    ordersPermissions.push({
      key: 'calls',
      label: <Link to={CALLS_PATH}>{t(strings.calls)}</Link>,
    });
  }
  if (PermissionsHelper?.getPermissions(PERMISSION_KEY_CART, ACTION_INDEX) && !!tenantConfig?.side_menu?.cp?.carts) {
    ordersPermissions.push({
      key: 'carts',
      label: <Link to={CARTS}>{t(strings.abandoned_carts)}</Link>,
    });
  }

  return ordersPermissions;
};

export const getCPKpiPermissions = (t) => {
  const kpiPermissions = [];
  if (PermissionsHelper.getPermissions(PERMISSION_KEY_FOLLOW_UP, ACTION_INDEX)) {
    kpiPermissions.push({
      key: 'todos',
      label: <Link to={FOLLOW_UPS_PATH}>{t(strings.todos)}</Link>,
    });
  }
  return kpiPermissions;
};

export const getCPPermissions = (t) => {
  const cpPermissions = [
    {
      key: 'dashboard',
      label: <Link to={DASHBOARD_PATH}>{t(strings.dashboard)}</Link>,
      icon: <IconDashboard size={20} />,
    },
  ];
  if (
    PermissionsHelper?.getPermissions(PERMISSION_KEY_DASHBOARD, ACTION_CURRENT_DAY_ORDER) ||
    PermissionsHelper?.getPermissions(PERMISSION_KEY_DASHBOARD, ACTION_LIFETIME_ORDER)
  ) {
    cpPermissions.push({
      key: 'real-time-statistics',
      label: <Link to={REAL_TIME_STATS_PATH}>{t(strings.real_time_statistics)}</Link>,
      icon: <TbDeviceAnalytics size={20} />,
    });
  }
  const ordersPermissions = getCPOrdersPermissions(t);
  if (hasAnyChildPermissions(ordersPermissions)) {
    if (!tenantConfig?.side_menu?.cp?.return_orders && ordersPermissions.length === 1) {
      cpPermissions.push({
        key: 'orders-listing',
        label: <Link to={tenantConfig?.side_menu?.cp?.orders?.path}>{t(strings.orders)}</Link>,
        icon: <IconOrdersList size={20} />,
      });
    } else {
      cpPermissions.push({
        key: strings.orders,
        label: t(strings.manage_orders),
        icon: <IconOrdersList size={20} />,
        className: styles.subMenu,
        children: ordersPermissions,
      });
    }
  }

  const sellerPermissions = getCPSellerPermissions(t);
  if (hasAnyChildPermissions(sellerPermissions)) {
    cpPermissions.push({
      key: strings.sellers,
      label: t(strings.sellers),
      icon: <IconUserSettings size={20} />,
      className: styles.subMenu,
      children: sellerPermissions,
    });
  }

  const kpisPermissions = getCPKpiPermissions(t);
  if (hasAnyChildPermissions(kpisPermissions)) {
    cpPermissions.push({
      key: 'Kpis',
      label: t(strings.kpis),
      icon: <TbCalendarStats size={20} />,
      className: styles.subMenu,
      children: kpisPermissions,
    });
  }

  const reportsPermissions = getCPReportsPermissions(t);
  if (hasAnyChildPermissions(reportsPermissions)) {
    cpPermissions.push({
      key: strings.reports,
      label: t(strings.reports),
      icon: <BsGraphUpArrow size={18} />,
      className: styles.subMenu,
      children: reportsPermissions,
    });
  }

  const productPermissions = getCPProductPermissions(t);
  if (hasAnyChildPermissions(productPermissions)) {
    cpPermissions.push({
      key: strings.products,
      label: t(strings.manage_products),
      icon: <IconProductsList size={20} />,
      className: styles.subMenu,
      children: productPermissions,
    });
  }

  // cpPermissions.push({
  //   key: 'unmapped-products',
  //   label: <Link to={UNMAPPED_PRODUCTS_PATH}>{t(strings.unmapped_products)}</Link>,
  //   icon: <IconProductsList size={20} />,
  // });
  const financePermissions = getCPFinancePermissions(t);
  if (hasAnyChildPermissions(financePermissions)) {
    cpPermissions.push({
      key: strings.finance,
      label: t(strings.finance),
      icon: <IconFinance size={20} />,
      className: styles.subMenu,
      children: financePermissions,
    });
  }
  const moderationPermissions = getCPModerationPermissions(t);
  if (!!tenantConfig?.moderation && hasAnyChildPermissions(moderationPermissions)) {
    cpPermissions.push({
      key: strings.moderation,
      label: t(strings.moderation),
      icon: <IconUserSettings size={20} />,
      className: styles.subMenu,
      children: moderationPermissions,
    });
  }

  const cpSettingsChildren = getCPSettingsChildren(t);
  if (hasAnyChildPermissions(cpSettingsChildren)) {
    cpPermissions.push({
      key: strings.settings,
      label: t(strings.settings),
      icon: <IconRoles size={20} />,
      className: styles.subMenu,
      children: cpSettingsChildren,
    });
  }
  return cpPermissions;
};

export const sellerSideMenuItems = (t, disabled = false) => {
  const items = [
    {
      key: 'dashboard',
      label: disabled ? (
        <span>{t(strings.dashboard)}</span>
      ) : (
        <Link to={OMS_DASHBOARD_PATH}>{t(strings.dashboard)}</Link>
      ),
      icon: <IconDashboard size={20} />,
      disabled,
    },
  ];
  items.push({
    key: strings.performance,
    label: disabled ? (
      <span>{t(strings.performance)}</span>
    ) : (
      <Link to={PERFORMANCE_DASHBOARD}>{t(strings.performance)}</Link>
    ),
    icon: <TbDeviceAnalytics size={20} />,
    className: styles.subMenu,
    disabled,
  });
  if (tenantConfig?.side_menu?.seller_center?.chat) {
    items?.push({
      key: 'chat',
      label: <Link to={CHAT_PATH}>{t(strings.chat)}</Link>,
      icon: <IoChatbubbleEllipsesOutline size={20} />,
    });
  }
  const productPermissions = getSellerCenterProductPermissions(t);
  if (hasAnyChildPermissions(productPermissions)) {
    items.push({
      key: strings.products,
      label: t(strings.products),
      icon: <IconProductsList size={20} />,
      className: styles.subMenu,
      children: productPermissions,
      disabled,
    });
  }
  const orderPermissions = getSellerCenterOrdersPermissions(t);
  if (hasAnyChildPermissions(orderPermissions)) {
    items.push({
      key: strings.orders,
      label: t(strings.orders),
      icon: <IconOrdersList size={20} />,
      className: styles.subMenu,
      children: orderPermissions,
    });
  }
  const financePermissions = getSellerCenterFinancePermissions(t);
  if (hasAnyChildPermissions(financePermissions)) {
    items.push({
      key: strings.finance,
      label: t(strings.finance),
      icon: <IconFinance size={20} />,
      className: styles.subMenu,
      children: financePermissions,
    });
  }
  const settingsPermissions = getSellerCenterSettingsPermissions(t);
  if (hasAnyChildPermissions(settingsPermissions)) {
    items.push({
      key: strings.account_settings,
      label: t(strings.account_settings),
      icon: <IconUserSettings size={20} />,
      className: styles.subMenu,
      children: settingsPermissions,
    });
  }
  return items;
};
export const cpSideMenuItems = (t, tenant) => getCPPermissions(t, tenant);
